import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import DashboardQueries from "src/Pages/Company/Dashboard/services/api/queryOptions";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { ImageDto } from "src/Pages/Company/Dashboard/services/api/api.models";
import useIconFileUpload from "./useIconFileUpload";
import { IconCropperRef } from "./IconCropper";

interface UseIconCropperOptions {
  iconValue: ImageDto;
  onCroppingChange?: (isCropping: boolean) => void;
  onUploadSuccess?: (data: { id: string; icon: ImageDto }) => void;
}

const useIconCropper = ({
  iconValue,
  onCroppingChange,
  onUploadSuccess,
}: UseIconCropperOptions) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState<string | null>(null);
  const companyId = useCompanyId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const iconCropperRef = useRef<IconCropperRef>(null);
  const uploadResolverRef = useRef<(() => void) | null>(null);

  const { mutate: uploadIcon, isPending: isUploading } = useMutation({
    ...DashboardQueries.uploadCompanyRedirectsWidgetIcon(companyId),
    onSuccess: (data) => {
      onUploadSuccess?.(data);
      uploadResolverRef.current?.();
      uploadResolverRef.current = null;
    },
    onError: () => {
      uploadResolverRef.current?.();
      uploadResolverRef.current = null;
    },
  });

  const handleFileReady = useCallback(
    (dataUrl: string) => {
      setCropperImageSrc(dataUrl);
      onCroppingChange?.(true);
    },
    [onCroppingChange]
  );

  const handleCropped = useCallback(
    (file: File) => {
      setCropperImageSrc(null);
      onCroppingChange?.(false);
      uploadIcon(file);
    },
    [uploadIcon, onCroppingChange]
  );

  const handleCancelCrop = useCallback(() => {
    setCropperImageSrc(null);
    onCroppingChange?.(false);
  }, [onCroppingChange]);

  const isCropping = useCallback(
    () => !!cropperImageSrc,
    [cropperImageSrc]
  );

  const submitCrop = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (!iconCropperRef.current || !cropperImageSrc) {
          resolve();
          return;
        }
        uploadResolverRef.current = resolve;
        iconCropperRef.current.submitCrop();
      }),
    [cropperImageSrc]
  );

  const { uploadError, handleFileUpload } = useIconFileUpload(handleFileReady);

  const iconSrc = iconValue?.original ?? null;

  useEffect(() => {
    setImgLoaded(false);
  }, [iconSrc]);

  return {
    imgLoaded,
    setImgLoaded,
    cropperImageSrc,
    isUploading,
    fileInputRef,
    iconCropperRef,
    uploadError,
    handleFileUpload,
    handleCropped,
    handleCancelCrop,
    isCropping,
    submitCrop,
  };
};

export default useIconCropper;
